import { ThunkAction } from 'redux-thunk';
import { Dispatch } from "react-redux";
import axios, { AxiosRequestConfig, AxiosPromise, CancelToken } from "axios";
import * as moment from "moment";
import * as qs from 'qs';

import { IApplicationState } from "../";
import { IAuthState } from "./state";
import { IToken } from '../../models';

// interfaces

export interface IAuthCheck {
    isValid: boolean,
    message: string,
    token?: IToken,
    next: string
}

interface IRefreshTokenResponse {
    data: {
        success: boolean,
        token: IToken
    }
}

export interface IHeader {
    name: string,
    value: string
}

export interface IGet {
    url: string,
    headers?: IHeader[],
    responseType?: string,
    cancelToken?: CancelToken,
}

export interface IPost {
    url: string,
    headers?: IHeader[],
    data?: any,
    responseType?: string,
}

export interface IPut {
    url: string,
    headers?: IHeader[],
    data?: any,
    responseType?: string,
}

export interface IDelete {
    url: string,
    headers?: IHeader[]
}

export interface IRemainingTime {
    token: {
        minutes: number,
        seconds: number
    };

    refreshToken: {
        minutes: number,
        seconds: number
    };
}

// action types

export const ActionTypes = {
    STORE_AUTH: "STORE_AUTH",
    CLEAR_AUTH: "CLEAR_AUTH",
    UPDATE_AUTH: "UPDATE_AUTH"
};

// action creators

export const ActionCreators = {
    storeAuth: (token: IToken, tokenHex: string) => {
        return { type: ActionTypes.STORE_AUTH, token, tokenHex };
    },
    clearAuth: () => {
        return { type: ActionTypes.CLEAR_AUTH };
    },
    updateAuth: (token: IToken) => {
        return { type: ActionTypes.UPDATE_AUTH, token };
    },
    apiGet: (options: IGet): ThunkAction<AxiosPromise<any>, IApplicationState, null> => (dispatch: Dispatch<IApplicationState>) => {
        return dispatch(ActionCreators.checkToken()).then((response: any) => {
            var token = response.token.access_token;

            var getConfig: AxiosRequestConfig = {
                headers: {
                    "Authorization": "Bearer " + token,
                    "Content-Type": "application/json",
                },
                responseType: options.responseType,
                cancelToken: options.cancelToken,
            };

            if (options.headers != null) {
                options.headers.map(header => {
                    getConfig.headers[header.name] = header.value;
                });
            }

            return axios.get(options.url, getConfig).then((result) => {
                return result;
            });
        });
    },
    apiGetPublic: (options: IGet): ThunkAction<AxiosPromise<any>, IApplicationState, null> => (dispatch: Dispatch<IApplicationState>) => {
        let getConfig: AxiosRequestConfig = {
            headers: {
                "Content-Type": "application/json",
            },
            responseType: options.responseType
        };

        if (options.headers != null) {
            options.headers.map(header => {
                getConfig.headers[header.name] = header.value;
            });
        }

        return axios.get(options.url, getConfig).then((result) => {
            return result;
        });
    },
    apiGetWithType: <Type>(options: IGet): ThunkAction<AxiosPromise<Type>, IApplicationState, null> => (dispatch: Dispatch<IApplicationState>) => {
        return dispatch(ActionCreators.checkToken()).then((response: any) => {
            var token = response.token.access_token;

            var getConfig: AxiosRequestConfig = {
                headers: {
                    "Authorization": "Bearer " + token,
                    "Content-Type": "application/json",
                },
                responseType: options.responseType
            };

            if (options.headers != null) {
                options.headers.map(header => {
                    getConfig.headers[header.name] = header.value;
                });
            }

            return axios.get<Type>(options.url, getConfig).then((result) => {
                return result;
            });
        });
    },
    apiPost: (options: IPost): ThunkAction<AxiosPromise<any>, IApplicationState, null> => (dispatch: Dispatch<IApplicationState>) => {
        return dispatch(ActionCreators.checkToken()).then((response: any) => {
            var token = response.token.access_token;

            var postConfig: AxiosRequestConfig = {
                headers: {
                    "Authorization": "Bearer " + token,
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                },
                responseType: options.responseType
            };

            if (options.headers != null) {
                options.headers.map((header, index) => {
                    postConfig.headers[header.name] = header.value;
                });
            }

            return axios.post(options.url, options.data, postConfig).then((result: any) => {
                return result;
            });
        });
    },
    apiPostFormData: (options: IPost): ThunkAction<AxiosPromise<any>, IApplicationState, null> => (dispatch: Dispatch<IApplicationState>) => {
        return dispatch(ActionCreators.checkToken()).then((response: any) => {
            var token = response.token.access_token;

            var postConfig: AxiosRequestConfig = {
                headers: {
                    "Authorization": "Bearer " + token
                }
            };

            if (options.headers != null) {
                options.headers.map((header, index) => {
                    postConfig.headers[header.name] = header.value;
                });
            }

            return axios.post(options.url, options.data, postConfig);
        });
    },
    apiPostPublic: (options: IPost): ThunkAction<AxiosPromise<any>, IApplicationState, null> => (dispatch: Dispatch<IApplicationState>) => {
        var postConfig: AxiosRequestConfig = {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
            },
            responseType: options.responseType
        };

        if (options.headers != null) {
            options.headers.map((header, index) => {
                postConfig.headers[header.name] = header.value;
            });
        }

        return axios.post(options.url, options.data, postConfig).then((result: any) => {
            return result;
        });
    },
    apiPutFormData: (options: IPost): ThunkAction<AxiosPromise<any>, IApplicationState, null> => (dispatch: Dispatch<IApplicationState>) => {
        return dispatch(ActionCreators.checkToken()).then((response: any) => {
            var token = response.token.access_token;

            var postConfig: AxiosRequestConfig = {
                headers: {
                    "Authorization": "Bearer " + token
                }
            };

            if (options.headers != null) {
                options.headers.map((header, index) => {
                    postConfig.headers[header.name] = header.value;
                });
            }

            return axios.put(options.url, options.data, postConfig).then((result: any) => {
                return result;
            }).catch((error: any) => {
                return error;
            });
        });
    },
    apiPut: (options: IPut): ThunkAction<AxiosPromise<any>, IApplicationState, null> => (dispatch: Dispatch<IApplicationState>) => {
        return dispatch(ActionCreators.checkToken()).then((response: any) => {
            var token = response.token.access_token;

            var config: AxiosRequestConfig = {
                headers: {
                    "Authorization": "Bearer " + token,
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                }
            };

            if (options.headers != null) {
                options.headers.map((header, index) => {
                    config.headers[header.name] = header.value;
                });
            }

            return axios.put(options.url, options.data, config).then((result: any) => {
                return result;
            }).catch((error: any) => {
                return error;
            });
        });
    },
    apiPutPublic: (options: IPut): ThunkAction<AxiosPromise<any>, IApplicationState, null> => (dispatch: Dispatch<IApplicationState>) => {
        let postConfig: AxiosRequestConfig = {
            headers: {
                "Content-Type": "application/json",
            },
            responseType: options.responseType
        };

        if (options.headers != null) {
            options.headers.map((header, index) => {
                postConfig.headers[header.name] = header.value;
            });
        }

        return axios.put(options.url, options.data, postConfig).then((result: any) => {
            return result;
        }).catch(error => {
            throw error;
        });
    },
    apiDelete: (options: IDelete): ThunkAction<AxiosPromise<any>, IApplicationState, null> => (dispatch: Dispatch<IApplicationState>) => {
        return dispatch(ActionCreators.checkToken()).then((response: any) => {
            var token = response.token.access_token;

            var config: AxiosRequestConfig = {
                headers: {
                    "Authorization": "Bearer " + token,
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                }
            };

            if (options.headers != null) {
                options.headers.map((header, index) => {
                    config.headers[header.name] = header.value;
                });
            }

            return axios.delete(options.url, config).then((result: any) => {
                return result;
            }).catch((error: any) => {
                return error;
            });
        });
    },
    apiDeletePublic: (options: IDelete): ThunkAction<AxiosPromise<any>, IApplicationState, null> => (dispatch: Dispatch<IApplicationState>) => {
        let config: AxiosRequestConfig = {
            headers: {
                "Content-Type": "application/json",
            },
        };

        if (options.headers != null) {
            options.headers.map((header, index) => {
                config.headers[header.name] = header.value;
            });
        }

        return axios.delete(options.url, config).then((result: any) => {
            return result;
        }).catch((error: any) => {
            return error;
        });
    },
    validToken: () => (dispatch: Dispatch<IApplicationState>, getState: () => IApplicationState): boolean => {
        var auth = getState().auth;

        if (auth.isAuthenticated) {
            var remainingTime: any = ActionCreators.getRemainingTime(auth);

            return !(remainingTime.token.minutes <= 0);
        } else {
            return false;
        }
    },
    checkToken: () => (dispatch: Dispatch<IApplicationState>, getState: () => IApplicationState): Promise<IAuthCheck> => {
        var auth = getState().auth;

        if (auth.isAuthenticated) {
            var remainingTime = ActionCreators.getRemainingTime(auth);

            if (remainingTime.token.minutes <= 5) {
                return dispatch(ActionCreators.refreshToken());
            } else {
                var r: IAuthCheck = {
                    isValid: true,
                    message: "",
                    token: auth.authInfo,
                    next: ""
                };

                return Promise.resolve(r);
            }
        }

        return Promise.resolve(null);
    },
    refreshToken: () => (dispatch: Dispatch<IApplicationState>, getState: () => IApplicationState): Promise<IAuthCheck> => {
        var state = getState();
        var refresh_token = (state.auth.authInfo != null ? state.auth.authInfo.refresh_token : "");
        var token_hex = state.auth.tokenHex;
        var username = (state.app.user != null ? state.app.user.username : "");

        var url = __API__ + "/token";

        var data = {
            username,
            client_id: __CLIENTID__,
            client_secret: __CLIENTSECRET__,
            refresh_token,
            token_hex,
            grant_type: "refresh_token",
        };

        return axios.post(url, qs.stringify(data)).then((response: IRefreshTokenResponse) => {
            var { success, token } = response.data;

            if (success) {
                dispatch(ActionCreators.updateAuth(token));

                var r: IAuthCheck = {
                    isValid: true,
                    message: "",
                    token,
                    next: ""
                };

                return Promise.resolve(r);
            } else {
                var r: IAuthCheck = {
                    isValid: false,
                    message: "Invalid refresh token",
                    token: undefined,
                    next: ""
                };

                return Promise.resolve(r);
            }
        }, error => {
            var r: IAuthCheck = {
                isValid: false,
                message: "Invalid refresh token",
                token: undefined,
                next: ""
            };

            return Promise.resolve(r);
        });
    },
    getRemainingTime: (auth: IAuthState): IRemainingTime => {
        var results = {
            token: { minutes: 0, seconds: 0 },
            refreshToken: { minutes: 0, seconds: 0 }
        };

        if (auth.isAuthenticated) {
            var existingToken = auth.authInfo;

            var expires = moment.utc(existingToken!.expires);

            var now = moment().utc();
            var minutes = expires.diff(now.format(), "minutes")
            var seconds = expires.diff(now.format(), "seconds") - (minutes * 60);

            var refreshExpires = moment.utc(existingToken!.refresh_expires);
            var refreshMinutes = refreshExpires.diff(now.format(), "minutes");
            var refreshSeconds = refreshExpires.diff(now.format(), "seconds") - (refreshMinutes * 60);

            results.token = {
                minutes,
                seconds
            };

            results.refreshToken = {
                minutes: refreshMinutes,
                seconds: refreshSeconds
            };
        }

        return results;
    }
};
